<template>
  <p-container>
    <p-row>
      <p-col xs12 md6 lg2>
        <p-text-field v-model="filter.searchCriteria" clearable :label="$t('core.app.search')" context-icon="search"></p-text-field>
      </p-col>
      <p-col xs12 md6 lg2>
        <p-select v-model="filter.statusId" clearable :label="$t('core.page.user.action.status')" :items="verifiedSelect"></p-select>
      </p-col>
    </p-row>
    <p-list-view ref="ordersList" v-model="list" :pagination.sync="pagination" :service-function="populateData" :columns="columns">
      <template slot="table-actions">
        <p-button @click="refresh">
          <p-icon>refresh</p-icon>
        </p-button>
        <router-link to="/_admin/users/create">
          <p-button>
            {{ $t('core.page.user.action.newUser') }}
          </p-button>
        </router-link>
      </template>
      <template #entry-actions="{entry}">
        <p-button @click="gotoDetail(entry.id.value)">
          <p-icon>edit</p-icon>
        </p-button>
      </template>
    </p-list-view>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import ResponsePagingInfo from '../../../plugins/servicelayer/responseTypes/responsePagingInfo';
  import GetUsersRequestViewModel from '../../../services/v1/viewModel/request/Users/GetUsersRequestViewModel';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      pagination: {} as ResponsePagingInfo,
      filter: {
        searchCriteria: undefined as string | undefined,
        statusId: undefined as number | undefined,
      },
      innerPagination: {
        'paging.CurrentPage': undefined as number | undefined,
        'paging.PageSize': undefined as number | undefined,
        'paging.IncludeCount': undefined as boolean | undefined,
        'paging.NoPaging': undefined as boolean | undefined,
      },
      list: {},
      columns: [] as {}[],
    }),
    computed: {
      request() {
        return new GetUsersRequestViewModel().fromModel({ ...this.filter, ...this.innerPagination });
      },
      verifiedSelect(): {caption: string, id?: number}[] {
        return [
          {
            caption: this.$t('core.page.user.select.unverified'),
            id: 0,
          },
          {
            caption: this.$t('core.page.user.select.verified'),
            id: 1,
          },
          {
            caption: this.$t('core.page.user.select.blocked'),
            id: 2,
          },
          {
            caption: this.$t('core.page.user.select.disabled'),
            id: 3,
          },
        ];
      },
      querySearchCriteria() {
        return Vue.$router.currentRoute.query.searchCriteria;
      },
      queryStatusId() {
        return Vue.$router.currentRoute.query.statusId;
      },
    },
    watch: {
      filter: {
        deep: true,
        handler() {
          this.pagination.currentPage = 1;
          this.innerPagination['paging.CurrentPage'] = 1;
          this.$debounce(() => {
            this.refresh();
          }, 300, this)();
        },
      },
      request() {
        this.writeRequestToQuery();
      },
    },
    beforeMount() {
      this.getQueryFromRequest();
      this.pagination.currentPage = this.innerPagination['paging.CurrentPage'] !;
      this.pagination.pageSize = this.innerPagination['paging.PageSize'] !;
    },
    mounted() {
      this.columns = [
        { value: this.$t('core.page.user.list.id'), textAlign: 'left', field: 'id' },
        { value: this.$t('core.page.user.list.email'), textAlign: 'left', field: 'email' },
        { value: this.$t('core.page.user.list.username'), textAlign: 'left', field: 'userName' },
        {
          value: this.$t('core.page.user.list.role'), textAlign: 'left', field: 'appHostRoleId', format: (value:number) => this.$tEnum('core.page.user.appHostRoleId', value),
        },
        { value: this.$t('core.page.user.list.verificationCode'), textAlign: 'left', field: 'verificationCode' },
        {
          value: this.$t('core.page.user.list.verifiedOn'), textAlign: 'left', field: 'verifiedOn', format: (value:any) => this.$format.localDateAndTime(value),
        },
        {
          value: this.$t('core.page.user.list.status'), textAlign: 'left', field: 'statusId', format: (value:number) => this.$tEnum('core.page.user.statusId', value),
        },
      ];
    },
    methods: {
      gotoDetail(id: string | number) {
        this.$router.push(`/_admin/users/${id}`);
      },
      getQueryFromRequest() {
        const queryParams = new GetUsersRequestViewModel();
        queryParams.fromQuery(this.$routerUtils.getQueryParams());
        this.filter.searchCriteria = queryParams.searchCriteria;
        this.filter.statusId = queryParams.statusId;

        this.innerPagination['paging.CurrentPage'] = queryParams['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = queryParams['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = queryParams['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = queryParams['paging.NoPaging'];
      },
      async writeRequestToQuery() {
        this.$debounce(() => {
          this.$routerUtils.updateQueryParams(this.request.toQuery(), { preserve: false });
        }, 200, this.writeRequestToQuery)();
      },
      refresh() {
        const ordersList = this.$refs.ordersList as any;
        ordersList?.populateList();
      },
      populateData(params: any) {
        this.innerPagination['paging.CurrentPage'] = params.query['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = params.query['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = params.query['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = params.query['paging.NoPaging'];
        return this.$service.api.core.users.getUsers(this.request);
      },
    },
  });
</script>
